import type { JSX } from 'react';
import type { AudioLevels } from '@teams-avatar/shared';

/**
 * Die zwei Pegel, an denen sich "der Avatar antwortet nicht" aufloesen laesst:
 *
 *   hoert nichts              -> der Rueckweg aus dem Meeting kommt nicht an
 *   hoert, spricht nicht      -> seine Seite antwortet nicht
 *   spricht, im Meeting still -> ACS sendet nicht
 *
 * Jede der drei Ursachen fuehrt zu einem anderen naechsten Schritt. Ohne diese
 * Anzeige war das nur per SSH und Messschleife zu beantworten.
 */

/** dBFS auf 0..1. Unter -60 dB ist Stille, ueber -6 dB ist laut. */
function fill(db: number): number {
  if (db <= -60) return 0;
  return Math.min(1, (db + 60) / 54);
}

function Meter({ label, hint, db }: { label: string; hint: string; db: number }): JSX.Element {
  const active = db > -50;
  const percent = fill(db) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-fg">{label}</span>
        <span className={`tabular text-xs ${active ? 'text-fg' : 'text-fg-muted'}`}>
          {db <= -60 ? 'still' : `${db.toFixed(0)} dB`}
        </span>
      </div>
      <div
        className="mt-1.5 h-2 overflow-hidden rounded-full bg-bg-muted"
        role="meter"
        aria-label={label}
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full ${active ? 'bg-brand' : 'bg-border-strong'}`}
          style={{ width: `${percent.toFixed(1)}%`, transition: 'width 150ms linear' }}
        />
      </div>
      <p className="mt-1.5 text-xs text-fg-muted">{hint}</p>
    </div>
  );
}

export function AudioMeter({ audio }: { audio: AudioLevels | undefined }): JSX.Element {
  return (
    <div className="rounded-xl border border-border bg-bg p-4 sm:p-5">
      <p className="text-[11px] tracking-wide text-fg-muted uppercase">Ton</p>
      {audio === undefined ? (
        <p className="mt-2 text-sm text-fg-muted">
          Noch keine Pegel — der Worker startet. Sie erscheinen, sobald er laeuft.
        </p>
      ) : (
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Meter
            label="AVA hoert"
            hint="Der Meeting-Ton an seinem Mikrofon. Bleibt das still, waehrend jemand spricht, kommt der Rueckweg nicht an."
            db={audio.hears}
          />
          <Meter
            label="AVA spricht"
            hint="Was nach Teams gesendet wird. Bleibt das still, waehrend er antwortet, geht seine Stimme nicht raus."
            db={audio.speaks}
          />
        </div>
      )}
    </div>
  );
}
